import React from 'react'
import { useQuery } from '@apollo/client'
import { PageSection as Section, Grid, LoadingCard, PageHeader } from '@superhi/design'
import { useRouter } from 'next/router'

import ROUTE from './route'
import COURSES from './queries'
import { DIFFICULTY_LEVEL_OPTIONS, SUBJECT_OPTIONS } from './config'

import { API } from 'src/api'
import CourseCollections from 'src/components/sections/CourseCollections'
import Aside from 'src/components/Aside'
import Main from 'src/components/Main'
import Page from 'src/components/Page'
import NoResults from 'src/components/NoResults'
import CourseCard from 'src/components/CourseCard'
import SEO from 'src/components/SEO'
import Results from 'src/components/Results'
import { PageConfig } from 'src/routing/types'
import Data from 'src/components/Data'

const PER_PAGE_DEFAULT = 44
const TITLE = 'Courses'

type FiltersConfig = {
  subject?: string
  difficultyLevel?: string
}

const LOADING_ITEMS: null[] = new Array(12).fill(null)

const GRID_1_CELL_SPAN_1: React.ComponentProps<typeof Grid.Cell>['span'] = {
  small: 'full',
  medium: 4,
  large: 4,
}

const Courses: PageConfig<unknown> = () => {
  const router = useRouter()
  const perPage = (router.query.perPage as unknown as number) || PER_PAGE_DEFAULT

  const { data, loading } = useQuery<API.CoursesCourses, API.CoursesCoursesVariables>(COURSES, {
    variables: {
      difficultyLevel: router.query.difficultyLevel as unknown as API.ProductCourseDifficultyLevel,
      subject: router.query.subject as unknown as API.ProductCourseSubject,
      page: router.query.page as unknown as number,
      perPage,
    },
    ssr: false,
  })

  return (
    <Page>
      <SEO
        title={TITLE}
        description="Our selection of online courses for creative people are designed to get you learning new skills quickly."
      />

      <Main>
        <PageHeader title={TITLE} backgroundVersion="BLUE_01" />

        <Results<FiltersConfig>
          name="courses-results"
          numResults={data?.productCourses?.totalCount || 0}
          filters={{
            subject: SUBJECT_OPTIONS,
            difficultyLevel: DIFFICULTY_LEVEL_OPTIONS,
          }}
          query={router.query}
          perPage={perPage}
          isLoading={loading}
        >
          <Section>
            <Grid alignItems="stretch">
              <Data
                data={data?.productCourses?.nodes!}
                loading={loading}
                whenLoading={
                  <>
                    {LOADING_ITEMS.map((_, i) => (
                      <Grid.Cell key={i} span={GRID_1_CELL_SPAN_1}>
                        <LoadingCard />
                      </Grid.Cell>
                    ))}
                  </>
                }
                isEmpty={(data) => data?.length === 0}
                whenEmpty={
                  <Grid.Cell span="full">
                    <NoResults hAlign="center" />
                  </Grid.Cell>
                }
              >
                {(courses) => (
                  <>
                    {courses.map((course, i) => (
                      <Grid.Cell key={course.slug} span={GRID_1_CELL_SPAN_1}>
                        <CourseCard {...course} imageLazy={i > 2} headingLevel={2} />
                      </Grid.Cell>
                    ))}
                  </>
                )}
              </Data>
            </Grid>
          </Section>
        </Results>
      </Main>
      <Aside>
        <CourseCollections />
      </Aside>
    </Page>
  )
}

Courses.route = ROUTE

export default Courses
